import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import clsx from "clsx";
import * as yup from "yup";
import randomColor from "randomcolor";
import { useStore } from "../../../stores";
import {
  ColorInput,
  CreatableSelect,
  DatePicker,
  Modal,
} from "../../../components";
import locale from "../../../constants/locale";
import styles from "./ChatInfo.module.scss";
import { Tooltip } from "antd";
import { DateTime } from "luxon";
import { toast } from "bulma-toast";

const Labels = observer(({ disabled }: { disabled?: boolean }) => {
  const { t } = useTranslation();
  const { labels, loadLabels } = useStore("bots");
  const { selectedChat, setLabels } = useStore("chats");
  const [modalOpen, setModalOpen] = useState(false);

  const canLoad = !labels.length && selectedChat;

  useEffect(() => {
    if (!canLoad) {
      return;
    }

    loadLabels();
  }, [canLoad, loadLabels]);

  if (!selectedChat) {
    return null;
  }

  const removeLabel = async (id: string) => {
    await setLabels(id, "remove");
  };

  return (
    <div>
      <span className="label has-text-grey">{t(locale.allLabels)}</span>
      <button
        disabled={disabled}
        className="button is-rounded is-ghosted mb-4 is-small"
        onClick={() => setModalOpen(true)}
      >
        <div className="icon" style={{ position: "absolute" }}>
          <img style={{ width: 12 }} src="/assets/add-blue.svg" alt="" />
        </div>
      </button>

      {selectedChat.labels?.map((m) => {
        const label = labels.find((f) => f.id === m) as any;

        if (!label?.color || !label?.name) {
          return null;
        }

        const labelDeadlines = JSON.parse(
          selectedChat.data?.labelDeadlines || "{}"
        );

        const deadline = labelDeadlines[label.id];

        return (
          <div
            key={label.id}
            className={clsx(styles.labelDiv, "columns is-mobile")}
          >
            <div
              className="column is-clickable px-1 py-3"
              style={{
                width: "12%",
                flex: "none",
              }}
            >
              <div
                className={clsx("mr-2")}
                style={{
                  borderRadius: "50%",
                  border: "2px solid " + label.color,
                  height: "1.2rem",
                  width: "1.2rem",
                }}
              />
            </div>
            <div
              className="column is-clickable px-0 py-3 has-long-text"
              style={{
                width: "76%",
                flex: "none",
                color: deadline ? "red" : undefined,
              }}
            >
              <Tooltip
                title={
                  deadline ? new Date(deadline).toLocaleDateString() : undefined
                }
              >
                {label.name}
              </Tooltip>
            </div>
            <div
              className="column px-1 py-3"
              style={{
                width: "12%",
                flex: "none",
              }}
            >
              <span
                className="icon is-clickable"
                onClick={() => {
                  if (!disabled) removeLabel(label.id);
                }}
              >
                <img src="/assets/close.svg" alt="close" />
              </span>
            </div>
          </div>
        );
      })}

      {modalOpen && (
        <CreateLabelModal
          open={true}
          onClose={() => setModalOpen(false)}
          disabled={disabled}
        />
      )}
    </div>
  );
});

const CreateLabelModal = observer(
  ({
    open,
    onClose,
    disabled,
  }: {
    open: boolean;
    onClose: () => void;
    disabled?: boolean;
  }) => {
    const { t } = useTranslation();
    const { selectedBot, labels, addLabel } = useStore("bots");
    const { selectedChat, setLabels } = useStore("chats");

    const availableLabels = useMemo(
      () =>
        labels
          .filter((f) => !selectedChat?.labels?.includes(f.id))
          .map((m) => ({ label: m.name, value: m.id, color: m.color })),
      [labels, selectedChat?.labels]
    );

    if (!selectedChat) {
      return null;
    }

    const schema = yup.object().shape({
      name: yup.mixed().required(t(locale.fieldRequired)),
    });

    return (
      <Formik
        initialValues={{
          name: undefined as { value: string; label: string } | undefined,
          color: randomColor(),
          deadline: undefined,
        }}
        validationSchema={schema}
        onSubmit={async ({ name, color, deadline }, actions) => {
          try {
            const label = await addLabel(name?.label.trim() || "", color);

            if (!label) {
              return;
            }

            if (selectedChat.labels?.includes(label)) {
              return;
            }

            await setLabels(
              label,
              "add",
              deadline
                ? DateTime.fromJSDate(deadline)
                    .setZone(selectedBot?.settings.timezone, {
                      keepLocalTime: true,
                    })
                    .startOf("day")
                    .toUTC()
                    .toISO()
                : undefined
            );

            actions.resetForm();
            onClose();
          } catch (error: any) {
            if (error.message === "labels-creation/restricted") {
              toast({
                message: t(locale.restrictedLabelCreation),
                type: "is-danger",
                dismissible: true,
                pauseOnHover: true,
              });
            }
          }
        }}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => (
          <Modal
            isOpen={open}
            onClose={onClose}
            title={t(locale.addLabel)}
            primaryText={t(locale.add)}
            disablePrimary={isSubmitting || disabled}
            onPrimary={() => submitForm()}
            allowCardOverflow
            zIndex={999999999}
          >
            <CreatableSelect
              label={t(locale.label)}
              name="name"
              className={clsx(styles.labelCreateable)}
              icon={() => (
                <div style={{ marginTop: "-0.8rem" }}>
                  <ColorInput
                    name="color"
                    disabled={
                      isSubmitting ||
                      labels.some((s) => s.id === values.name?.value)
                    }
                  />
                </div>
              )}
              options={availableLabels}
              placeholder={t(locale.addLabel)}
              onChange={(v: any) => {
                if (v.color) {
                  setFieldValue("color", v.color);
                }
              }}
              disabled={isSubmitting || disabled}
              formatOptionLabel={({ color, label }, meta) => (
                <div className="is-flex is-align-items-center">
                  {meta.context === "menu" && (
                    <div
                      className={clsx("mr-2")}
                      style={{
                        borderRadius: "50%",
                        border: "2px solid " + color,
                        height: "1.2rem",
                        width: "1.2rem",
                      }}
                    />
                  )}
                  <span className="is-flex-grow-1">{label}</span>
                </div>
              )}
            />
            <DatePicker
              name="deadline"
              label={t(locale.deadline)}
              dateFormat="dd/MM/yyyy"
              disabled={isSubmitting || disabled}
              minDate={DateTime.now().plus({ day: 1 }).toJSDate()}
              noControl
              wrapperClassName="w-100"
            />
          </Modal>
        )}
      </Formik>
    );
  }
);

export { CreateLabelModal };
export default Labels;
